// frontend/src/components/HoursManagement/index.js

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Clock, Save, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../Auth';
import { getWeekDates } from '../../utils/dateUtils';
import WeekNavigation from './WeekNavigation';
import ProjectsTable from './ProjectsTable';
import MobileHoursView from './MobileHoursView';

const apiBaseUrl = process.env.NODE_ENV === 'development'
  ? `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}`
  : `${process.env.REACT_APP_API_URL}/api` || 'http://localhost:8000';

const SAVE_STATUS = {
  IDLE: 'idle',
  UNSAVED: 'unsaved',
  SAVING: 'saving',
  ERROR: 'error',
};

const HoursManagement = () => {
  // State declarations (same as before)
  const [selectedWeek, setSelectedWeek] = useState(new Date());
  const [projects, setProjects] = useState([]);
  const [slots, setSlots] = useState([]);
  const [hours, setHours] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [needsSave, setNeedsSave] = useState(false);
  const [showOtherProjects, setShowOtherProjects] = useState(false);
  const [activeProjects, setActiveProjects] = useState([]);
  const [inactiveProjects, setInactiveProjects] = useState([]);
  const [objectivesByProject, setObjectivesByProject] = useState({});
  const [currentSlot, setCurrentSlot] = useState(null);
  const [plannedHoursByProject, setPlannedHoursByProject] = useState({});
  const [slotHoursByProject, setSlotHoursByProject] = useState({});
  const [focusProjectIds, setFocusProjectIds] = useState([]);
  const [projectsWithHours, setProjectsWithHours] = useState([]);
  const { user, getAuthHeaders } = useAuth();
  const [employee, setEmployee] = useState(null);
  const [saveStatus, setSaveStatus] = useState(SAVE_STATUS.IDLE);
  const [lastSaveError, setLastSaveError] = useState(null);
  const [projectSearch, setProjectSearch] = useState('');

  const saveTimeoutRef = useRef(null);
  const hoursRef = useRef(hours);
  const serverHoursRef = useRef({});
  const location = useLocation();

  // Update ref when hours state changes
  useEffect(() => {
    hoursRef.current = hours;
  }, [hours]);

  // --- Helper function to get local YYYY-MM-DD string ---
  const getLocalDateString = useCallback((date) => {
      if (!date) return '';
      // Use 'sv-SE' locale for guaranteed YYYY-MM-DD format based on local time
      return date.toLocaleDateString('sv-SE');
  }, []);
  // -------------------------------------------------------

  const getMonday = useCallback((d) => {
    const date = new Date(d);
    const day = date.getDay();
    const diff = date.getDate() - day + (day === 0 ? -6 : 1);
    date.setDate(diff);
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);

  const determineActiveProjects = useCallback((allProjects, focusIds, hoursIds) => {
    const focusSet = new Set((focusIds || []).map(Number));
    const hoursSet = new Set((hoursIds || []).map(Number));
    const activeIds = new Set();
    allProjects.forEach(project => {
      const status = (project?.status || '').toLowerCase();
      const isActive = status === 'in_progress';
      if ((isActive && focusSet.has(project.id)) || hoursSet.has(project.id)) {
        activeIds.add(project.id);
      }
    });

    let active = allProjects.filter(project => activeIds.has(project.id));
    let inactive = allProjects.filter(project => !activeIds.has(project.id));

    const horsProdProjectName = "Hors production";
    const horsProdProject = allProjects.find(p => p.name === horsProdProjectName);
    if (horsProdProject) {
      active = [horsProdProject, ...active.filter(p => p.id !== horsProdProject.id)];
      inactive = inactive.filter(p => p.id !== horsProdProject.id);
    }

    return { active, inactive };
  }, []);

  // Load initial data
  useEffect(() => {
    const fetchData = async () => {
        // ... (logic remains the same as previous correct version) ...
        try {
            setLoading(true);
            setError(null);
            // 1. Fetch employee
            const employeesResponse = await fetch(`${apiBaseUrl}/employees`, { headers: getAuthHeaders() });
            if (!employeesResponse.ok) throw new Error('Échec chargement employés');
            const employeesData = await employeesResponse.json();
            const matchingEmployee = employeesData.find(emp => emp.email === user.email);
            if (!matchingEmployee) throw new Error('Employé non trouvé');
            setEmployee(matchingEmployee);
            const employeeId = matchingEmployee.id;

            // 2. Fetch projects
            const projectsResponse = await fetch(`${apiBaseUrl}/projects`, { headers: getAuthHeaders() });
            if (!projectsResponse.ok) throw new Error('Échec chargement projets');
            const projectsData = await projectsResponse.json();
            const allProjects = Array.isArray(projectsData) ? projectsData : [];
            setProjects(allProjects);

            // 3. Fetch current slot details
            const monday = getMonday(selectedWeek);
            const slotsResponse = await fetch(`${apiBaseUrl}/slots`, { headers: getAuthHeaders() });
            if (!slotsResponse.ok) throw new Error('Échec chargement slots');
            const slotsData = await slotsResponse.json();
            const relevantSlot = slotsData.find(slot =>
              {
                const slotStart = new Date(slot.start_date); slotStart.setHours(0, 0, 0, 0);
                const slotEnd = new Date(slot.end_date); slotEnd.setHours(0, 0, 0, 0);
                // console.log(`DEBUG: Checking slot ${slot.id} (${slotStart.toISOString()} - ${slotEnd.toISOString()}) against ${monday.toISOString()}`);
                return monday >= slotStart && monday <= slotEnd;
              }
            );
            let slotWithDetails = null;
            if (relevantSlot) {
              const slotDetailsResponse = await fetch(`${apiBaseUrl}/slots/${relevantSlot.id}`, { headers: getAuthHeaders() });
              if (slotDetailsResponse.ok) slotWithDetails = await slotDetailsResponse.json();
              else console.warn(`Échec chargement détails slot ${relevantSlot.id}`);
            }
            setCurrentSlot(slotWithDetails);

            // 4. Determine focus-based projects for the employee
            const focusIds = new Set();
            await Promise.all(
              allProjects.map(async (project) => {
                if (!project?.circle_id) return;
                try {
                  const resp = await fetch(
                    `${apiBaseUrl}/circle-focus?parent_id=${project.circle_id}`,
                    { headers: getAuthHeaders() }
                  );
                  if (!resp.ok) return;
                  const focusEntries = await resp.json();
                  const token = `@[project:${project.id}|`;
                  if (Array.isArray(focusEntries) && focusEntries.some(entry =>
                    Number(entry.employee_id) === Number(employeeId) &&
                    typeof entry.focus_text === 'string' &&
                    entry.focus_text.includes(token)
                  )) {
                    focusIds.add(project.id);
                  }
                } catch (focusErr) {
                  // ignore
                }
              })
            );
            setFocusProjectIds(Array.from(focusIds));
          } catch (err) {
            setError(err.message || "Erreur chargement données");
            setActiveProjects([]); setInactiveProjects([]);
          } finally {
            setLoading(false);
          }
    };
    if (user?.email) { fetchData(); } else { setLoading(false); }
  }, [selectedWeek, user, getAuthHeaders, getMonday]);

  useEffect(() => {
    if (!projects.length) {
      setActiveProjects([]);
      setInactiveProjects([]);
      return;
    }
    const { active, inactive } = determineActiveProjects(projects, focusProjectIds, projectsWithHours);
    setActiveProjects(active);
    setInactiveProjects(inactive);
  }, [projects, focusProjectIds, projectsWithHours, determineActiveProjects]);

  // Load employee objectives
  const loadEmployeeObjectives = useCallback(async (employeeId, slot) => {
    // ... (logic remains the same as previous correct version) ...
     if (!employeeId || !slot || !slot.sprints || slot.sprints.length === 0) return;
    try {
      const projectIds = [...new Set(slot.sprints.map(sprint => Number(sprint.project_id)).filter(id => !isNaN(id)))];
      const sprintIds = slot.sprints.map(sprint => Number(sprint.id));
      const objectivesData = {};
      await Promise.all(projectIds.map(async (projectId) => {
        try {
          const objectivesResponse = await fetch(`${apiBaseUrl}/projects/${projectId}/objectives`, { headers: getAuthHeaders() });
          if (objectivesResponse.ok) {
            const projectObjectives = await objectivesResponse.json();
            const employeeObjectives = projectObjectives.filter(objective =>
              sprintIds.some(id => Number(id) === Number(objective.sprint_id)) &&
              Number(objective.owner_id) === Number(employeeId)
            );
            if (employeeObjectives.length > 0) objectivesData[projectId] = employeeObjectives;
          }
        } catch (error) { console.error(`Error fetching objectives for project ${projectId}:`, error); }
       }));
      setObjectivesByProject(objectivesData);
      let slotVelocityValue = slot.velocity ?? slot.token_velocity ?? null;
      if (slotVelocityValue === null && slot.id) {
        try {
          const workloadResponse = await fetch(`${apiBaseUrl}/slots/${slot.id}/workload`, { headers: getAuthHeaders() });
          if (workloadResponse.ok) slotVelocityValue = (await workloadResponse.json()).velocity;
        } catch (error) { console.error("Error fetching workload velocity:", error); }
      }
      const velocity = typeof slotVelocityValue === 'number' ? slotVelocityValue : parseFloat(slotVelocityValue) || 3.5;
      if (velocity > 0) {
        const plannedHours = {};
        Object.entries(objectivesData).forEach(([projectId, objectives]) => {
          const totalTokens = objectives.reduce((sum, objective) => sum + (parseFloat(objective.effort) || 0), 0);
          plannedHours[projectId] = Math.round((totalTokens / velocity) * 7.8 * 10) / 10;
        });
        setPlannedHoursByProject(plannedHours);
       } else { setPlannedHoursByProject({}); }
    } catch (error) { console.error('Error loading employee objectives:', error); }
  }, [getAuthHeaders]);

  // Load employee hours for the entire slot
  const loadEmployeeSlotHours = useCallback(async (employeeId, slot) => {
    // ... (logic remains the same as previous correct version) ...
     if (!employeeId || !slot) return;
    try {
      const startDate = getLocalDateString(new Date(slot.start_date)); // Use local date string
      const endDate = getLocalDateString(new Date(slot.end_date)); // Use local date string
      const response = await fetch(
        `${apiBaseUrl}/hours/employee/${employeeId}?start_date=${startDate}&end_date=${endDate}`,
        { headers: getAuthHeaders() }
      );
      if (!response.ok) throw new Error('Échec du chargement des heures du slot');
      const data = await response.json();
      const totalsByProject = {};
      Object.entries(data.hours_by_day || {}).forEach(([_, projectHours]) => {
        Object.entries(projectHours).forEach(([projectId, hours]) => { totalsByProject[projectId] = (totalsByProject[projectId] || 0) + hours; });
      });
      setSlotHoursByProject(totalsByProject);
    } catch (error) { console.error('Erreur chargement heures du slot:', error); }
  }, [getAuthHeaders, getLocalDateString]); // Added getLocalDateString

  // Load employee hours for the selected week
  const loadEmployeeHours = useCallback(async (employeeId, weekStart) => {
    if (!employeeId) return;
    try {
      const monday = getMonday(weekStart);
      const sunday = new Date(monday);
      sunday.setDate(monday.getDate() + 6);

      const startDateStr = getLocalDateString(monday); // Use local date string
      const endDateStr = getLocalDateString(sunday);   // Use local date string

      const response = await fetch(
        `${apiBaseUrl}/hours/employee/${employeeId}?start_date=${startDateStr}&end_date=${endDateStr}`,
        { headers: getAuthHeaders() }
      );

      if (!response.ok) throw new Error('Échec du chargement des heures');
      const data = await response.json();
      const serverHoursByEmployee = {};
      const hoursProjectIds = new Set();

      // Build a per-employee/project/day map from backend using its date keys
      Object.entries(data.hours_by_day || {}).forEach(([dateKey, projectHours]) => {
        Object.entries(projectHours).forEach(([projectId, h]) => {
          if (Number(h) > 0) {
            hoursProjectIds.add(Number(projectId));
          }
          if (!serverHoursByEmployee[projectId]) serverHoursByEmployee[projectId] = {};
          serverHoursByEmployee[projectId][dateKey] = h;
        });
      });
      setProjectsWithHours(Array.from(hoursProjectIds));
      serverHoursRef.current[employeeId] = serverHoursByEmployee;

      // Merge server hours into existing state without wiping unsaved local entries
      setHours(prev => {
        const prevForEmp = prev[employeeId] || {};
        // If server returned nothing, keep current state as-is
        if (Object.keys(serverHoursByEmployee).length === 0) {
          return prev;
        }
        const mergedForEmp = { ...prevForEmp };
        for (const [projId, byDay] of Object.entries(serverHoursByEmployee)) {
          const prevProj = prevForEmp[projId] || {};
          mergedForEmp[projId] = { ...prevProj, ...byDay };
        }
        return { ...prev, [employeeId]: mergedForEmp };
      });
    } catch (error) {
      console.error('Erreur chargement heures:', error);
      setError(error.message);
      setTimeout(() => setError(null), 3000);
    }
  }, [getMonday, getAuthHeaders, getLocalDateString]); // Added getLocalDateString

  // Effect to load objectives and slot hours
  useEffect(() => {
    if (employee?.id && currentSlot) {
      loadEmployeeObjectives(employee.id, currentSlot);
      loadEmployeeSlotHours(employee.id, currentSlot);
    }
  }, [employee?.id, currentSlot, loadEmployeeObjectives, loadEmployeeSlotHours]);

  // Effect to load week hours
  useEffect(() => {
    if (employee?.id) {
      loadEmployeeHours(employee.id, selectedWeek);
    }
  }, [employee?.id, selectedWeek, loadEmployeeHours]);

  const mondayCurrent = useMemo(() => getMonday(selectedWeek), [selectedWeek, getMonday]);

  const weekDates = useMemo(() => getWeekDates(mondayCurrent), [mondayCurrent]);

  const currentWeekDateKeys = useMemo(() => {
    return weekDates.map(date => getLocalDateString(date));
  }, [weekDates, getLocalDateString]);

  const filteredProjects = useMemo(() => {
    const query = projectSearch.trim().toLowerCase();
    if (!query) {
      return { active: activeProjects, inactive: inactiveProjects };
    }
    const matches = (project) => (project?.name || '').toLowerCase().includes(query);
    return {
      active: activeProjects.filter(matches),
      inactive: inactiveProjects.filter(matches),
    };
  }, [projectSearch, activeProjects, inactiveProjects]);

  const showOtherProjectsEffective = showOtherProjects || projectSearch.trim().length > 0;

  // Change week
  const changeWeek = useCallback((direction) => {
    setSelectedWeek(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
      return newDate;
    });
     setSaveStatus(SAVE_STATUS.IDLE);
     setNeedsSave(false);
  }, []);

  // Save workload data - Reads latest hours from Ref
  const saveWorkloadData = useCallback(async () => {
    if (!employee || saveStatus === SAVE_STATUS.SAVING) return;

    setSaveStatus(SAVE_STATUS.SAVING);
    setLastSaveError(null);

    const currentHoursState = hoursRef.current;
    const previousServerHoursForEmployee = serverHoursRef.current[employee.id] || {};

    try {
      const monday = getMonday(selectedWeek);
      const weekData = {
        employee_id: employee.id,
        week_start: getLocalDateString(monday), // Use local date string for week_start
        hours_by_day: {}
      };

      const weekDates = getWeekDates(monday);
      const currentHoursForEmployee = currentHoursState[employee.id] || {};
      const projectIdsToProcess = new Set([
        ...projects.map((proj) => Number(proj.id)).filter((id) => !Number.isNaN(id)),
        ...Object.keys(currentHoursForEmployee).map((id) => Number(id)).filter((id) => !Number.isNaN(id)),
        ...Object.keys(previousServerHoursForEmployee).map((id) => Number(id)).filter((id) => !Number.isNaN(id)),
      ]);

      weekDates.forEach(weekDate => {
        const weekDateStr = getLocalDateString(weekDate); // <<< Use local date string
        weekData.hours_by_day[weekDateStr] = {};
        projectIdsToProcess.forEach((projectId) => {
          const projKey = String(projectId);
          const currentValue = Number(currentHoursForEmployee[projKey]?.[weekDateStr] ?? 0);
          const previousValue = Number(previousServerHoursForEmployee[projKey]?.[weekDateStr] ?? 0);
          if (currentValue > 0) {
            weekData.hours_by_day[weekDateStr][projectId] = currentValue;
          } else if (previousValue > 0) {
            // Send explicit 0 to delete an existing server entry
            weekData.hours_by_day[weekDateStr][projectId] = 0;
          }
        });
      });

      const response = await fetch(
        `${apiBaseUrl}/hours/employee/${employee.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify(weekData),
          keepalive: true,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: `HTTP error ${response.status}` }));
        throw new Error(errorData.detail || 'Échec de la sauvegarde des heures');
      }

      const savedData = await response.json().catch(() => null);
      if (savedData?.hours_by_day) {
        const nextServerHours = {};
        Object.entries(savedData.hours_by_day).forEach(([dateKey, projectHours]) => {
          Object.entries(projectHours || {}).forEach(([projectId, h]) => {
            const numeric = Number(h);
            if (numeric > 0) {
              const key = String(projectId);
              if (!nextServerHours[key]) nextServerHours[key] = {};
              nextServerHours[key][dateKey] = numeric;
            }
          });
        });
        serverHoursRef.current[employee.id] = nextServerHours;
      }

      setSaveStatus(SAVE_STATUS.IDLE);
      setNeedsSave(false);

      if (currentSlot) {
        loadEmployeeSlotHours(employee.id, currentSlot);
      }

    } catch (error) {
      console.error('Erreur lors de la sauvegarde des heures:', error);
      setLastSaveError(error.message || 'Échec de la sauvegarde');
      setSaveStatus(SAVE_STATUS.ERROR);
    }
  }, [employee, selectedWeek, projects, currentSlot, getMonday, getAuthHeaders, loadEmployeeSlotHours, saveStatus, getLocalDateString]);


  // Handle hour input change - triggers state update and debounced save
  const handleHourChange = useCallback((projectId, date, value) => {
    if (!employee) return;

    const dateStr = getLocalDateString(date); // <<< Use local date string
    let parsed = parseFloat(String(value).replace(',', '.'));
    if (isNaN(parsed)) parsed = 0;
    const newValue = Math.max(0, Math.min(24, parsed));

    setHours(prev => {
      const newHoursState = { ...prev };
      const empHours = { ...(newHoursState[employee.id] || {}) };
      const projHours = { ...(empHours[projectId] || {}) };
      projHours[dateStr] = newValue;
      empHours[projectId] = projHours;
      newHoursState[employee.id] = empHours;
      return newHoursState;
    });

    setNeedsSave(true);
    setSaveStatus(SAVE_STATUS.UNSAVED);
    setLastSaveError(null);

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveWorkloadData();
    }, 1500);

  }, [employee, saveWorkloadData, getLocalDateString]);


  // Handle input blur - Trigger save immediately if needed
  const handleBlur = useCallback(() => {
      // Always cancel the debounce and trigger a save on blur.
      // This avoids relying on possibly stale state (needsSave) when onChange and onBlur fire in the same tick.
      if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
          saveTimeoutRef.current = null;
      }
      if (saveStatus !== SAVE_STATUS.SAVING) {
          saveWorkloadData();
      }
  }, [saveStatus, saveWorkloadData]);


  // Flush the currently focused input (if any) and force a save
  const flushAndSave = useCallback(() => {
    try {
      // Cancel any pending debounce
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }

      // If an hour input is focused, push its value into state/ref before saving
      const el = document.activeElement;
      if (el && el.tagName === 'INPUT' && el.dataset && el.dataset.hourInput === '1') {
        const projId = Number(el.dataset.projectId);
        const dateKey = el.dataset.date; // already in local YYYY-MM-DD from "sv-SE"
        let parsed = parseFloat(String(el.value).replace(',', '.'));
        if (isNaN(parsed)) parsed = 0;
        const clamped = Math.max(0, Math.min(24, parsed));

        if (employee?.id && projId && dateKey) {
          // Update state synchronously-ish and also the ref so save uses latest
          setHours(prev => {
            const newHoursState = { ...prev };
            const empHours = { ...(newHoursState[employee.id] || {}) };
            const projHours = { ...(empHours[projId] || {}) };
            projHours[dateKey] = clamped;
            empHours[projId] = projHours;
            newHoursState[employee.id] = empHours;
            return newHoursState;
          });
          // Update ref directly
          const current = hoursRef.current || {};
          const empHoursRef = { ...(current[employee.id] || {}) };
          const projHoursRef = { ...(empHoursRef[projId] || {}) };
          projHoursRef[dateKey] = clamped;
          empHoursRef[projId] = projHoursRef;
          hoursRef.current = { ...current, [employee.id]: empHoursRef };
        }
      }

      if (saveStatus !== SAVE_STATUS.SAVING) {
        saveWorkloadData();
      }
    } catch (e) {
      // Best-effort; don't crash on navigation
      console.warn('flushAndSave error', e);
    }
  }, [employee?.id, saveStatus, saveWorkloadData]);


  // Save when the tab becomes hidden or on pagehide
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        flushAndSave();
      }
    };
    const onPageHide = () => {
      flushAndSave();
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
    };
  }, [flushAndSave]);

  // Save when navigating away via router (cleanup runs on route change/unmount)
  useEffect(() => {
    return () => {
      flushAndSave();
    };
  }, [location, flushAndSave]);

  // Get hours for a specific cell (reads from state for display)
  const getHours = useCallback((employeeId, projectId, date) => {
    if (!employeeId) return 0;
    const dateStr = getLocalDateString(date); // <<< Use local date string
    return hours[employeeId]?.[projectId]?.[dateStr] || 0;
  }, [hours, getLocalDateString]); // Added getLocalDateString

  // Get total hours for a specific day (reads from state)
  const getDayTotal = useCallback((date) => {
    if (!employee?.id) return 0;
    const dateStr = getLocalDateString(date); // <<< Use local date string
    return Object.values(hours[employee.id] || {}).reduce((total, project) => {
      return total + (project[dateStr] || 0);
    }, 0);
  }, [hours, employee?.id, getLocalDateString]); // Added getLocalDateString

  // Get total hours for a specific project in the current week (reads from state)
  const getProjectTotal = useCallback((projectId) => {
    if (!employee?.id) return 0;
    const projectEntries = hours[employee.id]?.[projectId] || {};
    return currentWeekDateKeys.reduce((total, dateKey) => {
      return total + (projectEntries[dateKey] || 0);
    }, 0);
  }, [hours, employee?.id, currentWeekDateKeys]);


  // Add the beforeunload listener
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (needsSave || saveStatus === SAVE_STATUS.SAVING) {
        const message = 'Vous avez des modifications non enregistrées. Êtes-vous sûr de vouloir quitter ?';
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (saveTimeoutRef.current) {
           clearTimeout(saveTimeoutRef.current);
       }
    };
  }, [needsSave, saveStatus]);


  // --- Render Save Status Indicator (Simplified) ---
  const renderSaveStatus = () => {
    switch (saveStatus) {
      case SAVE_STATUS.SAVING:
      case SAVE_STATUS.UNSAVED:
        return (
          <div className="flex items-center text-sm text-blue-600" title="Modifications en attente ou enregistrement...">
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
            Modifications...
          </div>
        );
      case SAVE_STATUS.IDLE:
         return !needsSave ? (
             <div className="flex items-center text-sm text-green-600">
                 <CheckCircle className="h-4 w-4 mr-1" />
                 À jour
             </div>
         ) : null;
      case SAVE_STATUS.ERROR:
        return (
          <div className="flex items-center text-sm text-red-600" title={lastSaveError || 'Erreur sauvegarde'}>
            <XCircle className="h-4 w-4 mr-1" />
            Erreur
          </div>
        );
      default:
        return null;
    }
  };


  // --- Main Component Render ---

  if (loading) {
    return <div className="text-center p-4">Chargement...</div>;
  }

  if (error) {
    return <div className="text-red-600 p-4">{error}</div>;
  }


  return (
    <div className="bg-white shadow rounded-lg p-6 relative">

       {/* Save Status Indicator */}
        <div className="absolute top-4 right-4 z-10">
            {renderSaveStatus()}
        </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-6 pr-28">
        <h2 className="text-xl font-semibold flex items-center">
          <Clock className="h-5 w-5 mr-2" />
          Gestion des Heures
        </h2>
        {employee ? (
          <div className="flex items-center space-x-2">
            <span className="text-gray-600">Connecté :</span>
            <span className="font-medium">{employee.name}</span>
          </div>
        ) : (
          <div className="text-yellow-600">Employé non identifié</div>
        )}
      </div>

      <div className="flex items-center mb-4">
        <input
          type="search"
          value={projectSearch}
          onChange={(e) => setProjectSearch(e.target.value)}
          placeholder="Rechercher un projet"
          className="w-52 sm:w-60 border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* Week Navigation */}
      <WeekNavigation monday={mondayCurrent} changeWeek={changeWeek} />

      {/* Vue desktop/tablette */}
      <div className="hidden md:block">
        <ProjectsTable
          weekDates={weekDates}
          activeProjects={filteredProjects.active}
          inactiveProjects={filteredProjects.inactive}
          showOtherProjects={showOtherProjectsEffective}
          setShowOtherProjects={setShowOtherProjects}
          getHours={getHours}
          handleHourChange={handleHourChange}
          handleBlur={handleBlur}
          employee={employee}
          getProjectTotal={getProjectTotal}
          getDayTotal={getDayTotal}
          slotHoursByProject={slotHoursByProject}
          plannedHoursByProject={plannedHoursByProject}
        />
      </div>

      {/* Vue mobile dédiée */}
      <div className="md:hidden">
        <MobileHoursView
          weekDates={weekDates}
          activeProjects={filteredProjects.active}
          inactiveProjects={filteredProjects.inactive}
          showOtherProjects={showOtherProjectsEffective}
          setShowOtherProjects={setShowOtherProjects}
          getHours={getHours}
          handleHourChange={handleHourChange}
          handleBlur={handleBlur}
          employee={employee}
          plannedHoursByProject={plannedHoursByProject}
          slotHoursByProject={slotHoursByProject}
          getDayTotal={getDayTotal}
        />
      </div>
    </div>
  );
};

export default HoursManagement;
